/**
 * Sync Worker Client - Main Thread Interface
 *
 * ISSUE_038: Provides an API to control the SyncEngine running in a Web Worker.
 * All heavy sync operations happen off the main thread.
 *
 * @module core/sync/workerClient
 */

import type { SyncState } from "~/db/client";
import type { WorkspaceContext } from "~/services/sessions/sessionsService";
import type {
  WorkerMessage,
  WorkerResponse,
  InitPayload,
  SyncResult,
  SyncProgress,
  SyncDegradedPayload,
  AuthDeadPayload,
} from "./sync.worker";

// ============================================================================
// TYPES
// ============================================================================

export interface PushErrorInfo {
  sessionId: string;
  studentName: string;
  error: string;
}

export interface SyncWorkerClientConfig {
  workspace: WorkspaceContext;
  supabaseUrl: string;
  supabaseAnonKey: string;
  accessToken: string;
  autoSyncInterval?: number;
  /**
   * ISSUE_195 D5: 구독 만료 사용자의 **열람 전용** 동기화. pull 만 하고 push 는 하지 않는다.
   * 서버에 남아 있는 자기 데이터를 볼 수 있게 하되, 프리미엄인 다기기 쓰기 동기화는 잠근다.
   */
  readOnly?: boolean;
  onSyncStateChange?: (state: SyncState) => void;
  onSyncProgress?: (progress: SyncProgress) => void;
  onError?: (error: string) => void;
  onPushError?: (error: PushErrorInfo) => void;
  /** ISSUE_047: Callback to refresh access token when JWT expires */
  onTokenRefreshRequired?: () => Promise<string | null>;
  /**
   * ISSUE_164 조치 A: SyncWorker 가 누적 실패(>=3) 도달 시 메인에 통지.
   * UI 표면화는 후속 이슈 — 본 PR 에서는 콜백 인터페이스만 추가.
   * 미구독 시 Worker 로그만 남고 동작은 정상 (좀비 차단 목적은 달성).
   */
  onSyncDegraded?: (info: SyncDegradedPayload) => void;
  /**
   * ISSUE_164 조치 A: SyncWorker 가 자체 auth_dead 진입 시 메인에 통지.
   * AuthBroker forceLogout 가 인지 못한 좀비 상태도 자체 종결되도록.
   */
  onAuthDead?: (info: AuthDeadPayload) => void;
}

// ============================================================================
// SYNC WORKER CLIENT
// ============================================================================

export class SyncWorkerClient {
  private worker: Worker | null = null;
  private isReady = false;
  private pendingPromises: Map<string, {
    resolve: (value: unknown) => void;
    reject: (error: Error) => void;
    timeoutId: ReturnType<typeof setTimeout>;
  }> = new Map();
  private config: SyncWorkerClientConfig;
  // ISSUE_047: Epoch tracking for token synchronization
  private currentEpoch: number = 0;
  private lastAckedEpoch: number = 0;

  constructor(config: SyncWorkerClientConfig) {
    this.config = config;
  }

  /**
   * Update callbacks on existing instance
   * This is needed because getSyncWorkerClient() may be called multiple times
   * with different callbacks (e.g., React re-renders), but we want to keep
   * the same worker instance while updating the callbacks.
   */
  updateCallbacks(config: SyncWorkerClientConfig): void {
    // Only update callback functions, keep the rest of the config
    if (config.onSyncStateChange) {
      this.config.onSyncStateChange = config.onSyncStateChange;
    }
    if (config.onSyncProgress) {
      this.config.onSyncProgress = config.onSyncProgress;
    }
    if (config.onError) {
      this.config.onError = config.onError;
    }
    if (config.onPushError) {
      this.config.onPushError = config.onPushError;
    }
    if (config.onTokenRefreshRequired) {
      this.config.onTokenRefreshRequired = config.onTokenRefreshRequired;
    }
    if (config.onSyncDegraded) {
      this.config.onSyncDegraded = config.onSyncDegraded;
    }
    if (config.onAuthDead) {
      this.config.onAuthDead = config.onAuthDead;
    }
  }

  /**
   * Clear all pending promises with an error
   * Call this on worker crash or stop
   */
  private clearPendingPromises(error: string): void {
    for (const [, { reject, timeoutId }] of this.pendingPromises) {
      clearTimeout(timeoutId);
      reject(new Error(error));
    }
    this.pendingPromises.clear();
  }

  /**
   * Start the sync worker
   */
  async start(): Promise<void> {
    if (this.worker) {
      return;
    }

    // Create worker using Vite's worker import
    // This is dynamically imported to avoid SSR issues
    const WorkerConstructor = (await import("./sync.worker?worker")).default;
    this.worker = new WorkerConstructor();

    // Set up message handler
    this.worker.onmessage = (event: MessageEvent<WorkerResponse>) => {
      this.handleMessage(event.data);
    };

    this.worker.onerror = (error) => {
      console.error("[SyncWorkerClient] Worker error:", error);
      this.config.onError?.(error.message);
      // Reject all pending promises on worker crash
      this.clearPendingPromises(`Worker crashed: ${error.message}`);
      // Mark as not ready
      this.isReady = false;
    };

    // Initialize worker
    await this.sendAndWait<void>("init", {
      supabaseUrl: this.config.supabaseUrl,
      supabaseAnonKey: this.config.supabaseAnonKey,
      accessToken: this.config.accessToken,
      workspace: this.config.workspace,
      autoSyncInterval: this.config.autoSyncInterval ?? 30000,
      readOnly: this.config.readOnly === true, // ISSUE_195 D5
    } satisfies InitPayload, "ready");

    this.isReady = true;

    // Start sync loop
    await this.sendAndWait<void>("start", undefined, "started");
  }

  /**
   * Stop the sync worker
   */
  async stop(): Promise<void> {
    if (!this.worker) {
      return;
    }

    try {
      await this.sendAndWait<void>("stop", undefined, "stopped");
    } catch {
      // stop may fail if worker is already terminated
    }

    // Clear any remaining pending promises
    this.clearPendingPromises("Worker stopped");

    this.worker.terminate();
    this.worker = null;
    this.isReady = false;
  }

  /**
   * ISSUE_187: 즉시 종료 — graceful "stop" round-trip(최대 60s 타임아웃) 없이
   * worker thread를 바로 terminate. 학원 전환 재조준용(어차피 폐기할 worker라
   * 깔끔한 종료를 기다릴 필요 없음 + 메인 흐름 블로킹 위험 제거).
   */
  terminateNow(): void {
    this.clearPendingPromises("Worker terminated (academy switch)");
    if (this.worker) {
      this.worker.terminate();
      this.worker = null;
    }
    this.isReady = false;
  }

  /**
   * Trigger manual sync
   */
  async sync(): Promise<SyncResult> {
    if (!this.isReady) {
      throw new Error("Worker not ready");
    }

    return this.sendAndWait<SyncResult>("sync", undefined, "sync_result");
  }

  /**
   * Trigger manual push
   */
  async push(): Promise<SyncResult> {
    if (!this.isReady) {
      throw new Error("Worker not ready");
    }

    return this.sendAndWait<SyncResult>("push", undefined, "sync_result");
  }

  /**
   * Trigger manual pull
   */
  async pull(): Promise<SyncResult> {
    if (!this.isReady) {
      throw new Error("Worker not ready");
    }

    return this.sendAndWait<SyncResult>("pull", undefined, "sync_result");
  }

  /**
   * Check if worker is running
   */
  isRunning(): boolean {
    return this.worker !== null && this.isReady;
  }

  /**
   * Update access token (call when token is refreshed)
   * ISSUE_047: Added epoch parameter for Worker synchronization
   */
  updateAccessToken(newToken: string, epoch?: number): void {
    if (!this.worker || !this.isReady) {
      return;
    }
    // Send token with epoch for validation
    this.worker.postMessage({
      type: "update_token",
      payload: { token: newToken, epoch: epoch ?? Date.now() },
    });
    // Also update config for future use
    this.config.accessToken = newToken;
    // Track current epoch
    this.currentEpoch = epoch ?? Date.now();
  }

  /**
   * Notify worker that auth is dead — stop sync timer
   */
  notifyAuthDead(): void {
    if (!this.worker || !this.isReady) return;
    this.worker.postMessage({ type: "auth_dead" });
  }

  /**
   * ISSUE_047: Check if worker token is synced with given epoch
   */
  isTokenSynced(): boolean {
    return this.lastAckedEpoch >= this.currentEpoch;
  }

  /**
   * ISSUE_047: Get last acknowledged epoch from worker
   */
  getLastAckedEpoch(): number {
    return this.lastAckedEpoch;
  }

  // ==========================================================================
  // PRIVATE METHODS
  // ==========================================================================

  /**
   * ISSUE_047: Handle token refresh request from worker
   */
  private async handleTokenRefreshRequest(): Promise<void> {
    if (!this.config.onTokenRefreshRequired) {
      console.error("[SyncWorkerClient] No token refresh handler configured - check SyncInitializer");
      // 빈 토큰 전송 → Worker 가드가 클라이언트 재생성 없이 즉시 unblock
      this.worker?.postMessage({ type: "update_token", payload: "" });
      return;
    }

    try {
      const newToken = await this.config.onTokenRefreshRequired();

      if (newToken) {
        this.updateAccessToken(newToken);
      } else {
        console.error("[SyncWorkerClient] Token refresh returned null - session expired");
        // 빈 토큰 전송 → Worker 가드가 클라이언트 재생성 없이 즉시 unblock
        this.worker?.postMessage({ type: "update_token", payload: "" });
        this.config.onError?.("Session expired - please re-login");
      }
    } catch (err) {
      console.error("[SyncWorkerClient] Token refresh failed:", err);
      // 빈 토큰 전송 → Worker 가드가 클라이언트 재생성 없이 즉시 unblock
      this.worker?.postMessage({ type: "update_token", payload: "" });
      this.config.onError?.(`Token refresh failed: ${err}`);
    }
  }

  private handleMessage(response: WorkerResponse): void {
    const { type, payload } = response;

    switch (type) {
      case "sync_state":
        this.config.onSyncStateChange?.(payload as SyncState);
        break;

      case "sync_progress":
        this.config.onSyncProgress?.(payload as SyncProgress);
        break;

      case "error":
        this.config.onError?.(payload as string);
        // Reject any pending promises
        this.clearPendingPromises(payload as string);
        break;

      case "push_error":
        this.config.onPushError?.(payload as PushErrorInfo);
        break;

      case "token_refresh_required":
        // ISSUE_047: Worker requested token refresh
        this.handleTokenRefreshRequest();
        break;

      case "sync_degraded":
        // ISSUE_164 조치 A: 누적 실패 통지 — UI 표면화는 후속 이슈
        this.config.onSyncDegraded?.(payload as SyncDegradedPayload);
        break;

      case "auth_dead":
        // ISSUE_164 조치 A: Worker 자체 auth_dead 진입 — 메인이 인지 못한 좀비 차단
        this.config.onAuthDead?.(payload as AuthDeadPayload);
        break;

      case "ready":
      case "started":
      case "stopped":
      case "sync_result":
      case "token_updated": {
        const promise = this.pendingPromises.get(type);
        if (promise) {
          clearTimeout(promise.timeoutId);
          promise.resolve(payload);
          this.pendingPromises.delete(type);
        }
        break;
      }

      case "token_ack": {
        // ISSUE_047: Worker acknowledged token update
        const ackPayload = payload as { epoch: number };
        this.lastAckedEpoch = ackPayload.epoch;
        break;
      }
    }
  }

  private sendAndWait<T>(
    messageType: WorkerMessage["type"],
    payload: unknown,
    responseType: WorkerResponse["type"]
  ): Promise<T> {
    return new Promise((resolve, reject) => {
      if (!this.worker) {
        reject(new Error("Worker not initialized"));
        return;
      }

      // Timeout after 60 seconds
      const timeoutId = setTimeout(() => {
        if (this.pendingPromises.has(responseType)) {
          this.pendingPromises.delete(responseType);
          reject(new Error(`Timeout waiting for ${responseType}`));
        }
      }, 60000);

      this.pendingPromises.set(responseType, {
        resolve: resolve as (value: unknown) => void,
        reject,
        timeoutId,
      });

      const message: WorkerMessage = { type: messageType, payload };
      this.worker.postMessage(message);
    });
  }
}

// ============================================================================
// SINGLETON
// ============================================================================

let clientInstance: SyncWorkerClient | null = null;

/**
 * Check if worker client exists
 */
export function hasSyncWorkerClient(): boolean {
  return clientInstance !== null;
}

/**
 * Get or create worker client
 * If client already exists, updates callbacks to ensure latest handlers are used
 */
export function getSyncWorkerClient(config: SyncWorkerClientConfig): SyncWorkerClient {
  if (!clientInstance) {
    clientInstance = new SyncWorkerClient(config);
  } else {
    // Update callbacks on existing instance to handle React re-renders
    clientInstance.updateCallbacks(config);
  }
  return clientInstance;
}

/**
 * Clear worker client
 */
export async function clearSyncWorkerClient(): Promise<void> {
  if (clientInstance) {
    await clientInstance.stop();
    clientInstance = null;
  }
}

/**
 * ISSUE_187: worker client 즉시 종료 (학원 전환 재조준용 — graceful stop 대기 없음).
 * 로그아웃은 기존 graceful clearSyncWorkerClient() 유지; 이건 학원 갈아탈 때만.
 */
export function clearSyncWorkerClientImmediate(): void {
  if (clientInstance) {
    clientInstance.terminateNow();
    clientInstance = null;
  }
}

/**
 * Update access token for sync worker (call when token is refreshed)
 * ISSUE_047: Added epoch parameter for Worker synchronization
 */
export function updateSyncWorkerToken(newToken: string, epoch?: number): void {
  if (clientInstance) {
    clientInstance.updateAccessToken(newToken, epoch);
  }
}

/**
 * ISSUE_047: Check if worker token is synced
 */
export function isWorkerTokenSynced(): boolean {
  return clientInstance?.isTokenSynced() ?? true;
}

/**
 * Notify worker that auth is dead — stop sync timer
 */
export function notifySyncWorkerAuthDead(): void {
  if (clientInstance && clientInstance.isRunning()) {
    clientInstance.notifyAuthDead();
  }
}

/**
 * Trigger manual sync (call on recovery to re-sync data)
 */
export async function triggerSyncWorkerSync(): Promise<SyncResult | null> {
  if (clientInstance && clientInstance.isRunning()) {
    try {
      return await clientInstance.sync();
    } catch (err) {
      console.error("[SyncWorkerClient] Manual sync failed:", err);
      return null;
    }
  }
  return null;
}
